import React, { useState } from 'react';
import { runAllTests } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Play, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TestsPage = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRunTests = async () => {
    setLoading(true);
    setResults(null);
    try {
      const response = await runAllTests();
      setResults(response.data);
      if (response.data.verdict === 'PASS') {
        toast.success('All tests passed!');
      } else {
        toast.error('Some tests failed');
      }
    } catch (error) {
      toast.error('Failed to run tests');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="tests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Automated Tests</h1>
          <p className="text-sm text-zinc-500 font-inter">Verify system behavior and data integrity</p>
        </div>
        <Button 
          onClick={handleRunTests} 
          disabled={loading}
          className="rounded-sm"
          data-testid="run-tests-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Run All Tests
            </>
          )}
        </Button>
      </div>

      {/* Results */}
      {!results ? (
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-12 text-center">
            <Play className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
            <p className="text-zinc-500 font-inter">Click "Run All Tests" to verify system functionality</p>
            <p className="text-xs text-zinc-400 font-inter mt-2">
              Tests verify: Evidence hashing, Thread reuse, Draft approvals, Funnel updates, Strategy isolation, Deal/Vehicle linkage
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <Card className={`border shadow-none rounded-sm ${results.verdict === 'PASS' ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {results.verdict === 'PASS' ? (
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                  ) : (
                    <XCircle className="w-12 h-12 text-red-500" />
                  )}
                  <div>
                    <h2 className={`text-2xl font-bold font-manrope ${results.verdict === 'PASS' ? 'text-emerald-800' : 'text-red-800'}`}>
                      {results.verdict}
                    </h2>
                    <p className="text-sm font-inter text-zinc-600">
                      {results.summary.passed} of {results.summary.total} tests passed
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex gap-2">
                    <Badge className="bg-emerald-500 text-white">{results.summary.passed} Passed</Badge>
                    {results.summary.failed > 0 && (
                      <Badge className="bg-red-500 text-white">{results.summary.failed} Failed</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Individual Tests */}
          <Card className="border border-zinc-200 shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-manrope text-base">Test Results</CardTitle>
              <CardDescription className="font-inter">Detailed results for each test case</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {results.tests.map((test, index) => (
                <div 
                  key={index} 
                  className={`border rounded-sm p-4 ${test.passed ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}
                  data-testid={`test-result-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {test.passed ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium font-inter text-zinc-900">{test.name}</p>
                        {test.details && (
                          <div className="mt-2 text-xs text-zinc-600 font-mono space-y-1">
                            {Object.entries(test.details).map(([key, value]) => (
                              <p key={key}>
                                <span className="text-zinc-400">{key.replace(/_/g, ' ')}:</span>{' '}
                                <span className={value === true ? 'text-emerald-600' : value === false ? 'text-red-600' : ''}>
                                  {String(value)}
                                </span>
                              </p>
                            ))}
                          </div>
                        )}
                        {test.error && (
                          <p className="mt-2 text-xs text-red-600 font-mono">Error: {test.error}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={test.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                      {test.passed ? 'PASS' : 'FAIL'}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TestsPage;
